import styled, { css, keyframes } from 'styled-components'
import type { NodeCategory } from '@bandos/shared'
import { NODE_DEFAULTS } from '../../../../../utils/nodeDefaults'
import { getBorderLayerOffsets, getBorderLayerRadius } from '../../../../../utils/borderLayers'
import { NODE_SHARED_STYLES, TEXT_STYLES } from '../../../../../constants/mindmapConfig'
import { SPACING, TYPOGRAPHY, TRANSITIONS, FONT_WEIGHT } from '../../../../../styles/theme'
import { HandleButton } from '../../styles/HandleStyles'
import { hexToRgba } from '../nodeHelpers'

// Fade-in animation for expanded nodes
const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`

// Simple pulse - one ring expanding outward
const readyPulse = keyframes`
    0% {
        box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.6);
    }
    100% {
        box-shadow: 0 0 0 14px rgba(139, 92, 246, 0);
    }
`

// Extended HandleButton that pulses when suggestions are ready or criteria just met
export const PulsingHandleButton = styled(HandleButton)<{ $shouldPulse: boolean }>`
  ${(props) =>
    props.$shouldPulse &&
    css`
      animation: ${readyPulse} 0.7s ease-out forwards;
    `}
`

// Styled component for the node container
export const StyledNodeContainer = styled.div<{
  $category: NodeCategory
  $isFocused: boolean
  $borderLayers?: string[]
  $borderColorOverride?: string
  $hasDirectTag?: boolean
  $isCollapsed?: boolean
}>`
  /* When collapsed: transparent, no border, content grows RIGHT only */
  ${(props) =>
    props.$isCollapsed
      ? css`
          background: transparent;
          border: none;
          border-radius: 0;
          width: auto;
          min-height: auto;
          box-shadow: none;
          padding: 4px 8px;
          /* Ensure content left-aligns so growth happens to the right */
          align-items: flex-start;
        `
      : css`
          /* Fade in when expanding from collapsed state */
          animation: ${fadeIn} 0.8s ease forwards;

          /* Darker shell — main content inset is lighter */
          background: var(--color-node-bg-darker);

          /* Subtle neutral border - clean and modern */
          border: 1px solid
            ${props.$borderColorOverride
              ? props.$borderColorOverride + '40'
              : 'var(--color-node-border)'};
          border-radius: 8px;

          /* Outer border for nodes with direct tags */
          ${props.$hasDirectTag &&
          props.$borderColorOverride &&
          css`
            outline: 3px solid ${props.$borderColorOverride}30;
            outline-offset: 0px;
          `}

          width: ${NODE_DEFAULTS.width}px;
          max-width: ${NODE_DEFAULTS.maxWidth}px;
          min-height: ${NODE_DEFAULTS.minHeight}px;

          ${props.$isFocused
            ? css`
                /* Focused state - subtle lift */
                box-shadow: var(--shadow-node-focused);
                transform: translateY(-1px);
                z-index: 10;
              `
            : css`
                /* Default state - clean shadow */
                box-shadow: var(--shadow-node);
                transform: translateY(0px);
                z-index: 1;
              `}

          /* Hover state for non-focused nodes */
        &:hover {
            ${!props.$isFocused &&
            css`
              box-shadow: var(--shadow-node-hover);
            `}
          }

          /* Priority/Severity border layers using pseudo-elements */
          ${props.$borderLayers &&
          props.$borderLayers.length > 0 &&
          css`
            &::before {
              content: '';
              position: absolute;
              top: -${getBorderLayerOffsets().first}px;
              left: -${getBorderLayerOffsets().first}px;
              right: -${getBorderLayerOffsets().first}px;
              bottom: -${getBorderLayerOffsets().first}px;
              border: ${props.$borderLayers[0]};
              border-radius: ${getBorderLayerRadius(NODE_SHARED_STYLES.borderRadius).first};
              pointer-events: none;
              z-index: -1;
            }

            ${props.$borderLayers.length > 1 &&
            css`
              &::after {
                content: '';
                position: absolute;
                top: -${getBorderLayerOffsets().second}px;
                left: -${getBorderLayerOffsets().second}px;
                right: -${getBorderLayerOffsets().second}px;
                bottom: -${getBorderLayerOffsets().second}px;
                border: ${props.$borderLayers[1]};
                border-radius: ${getBorderLayerRadius(NODE_SHARED_STYLES.borderRadius).second};
                pointer-events: none;
                z-index: -2;
              }
            `}
          `}
        `}

  font-size: ${NODE_SHARED_STYLES.fontSize};
  font-weight: ${NODE_SHARED_STYLES.fontWeight};
  cursor: grab;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
  transition: all 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  /* Allow pinch-zoom on mobile while preserving single-finger drag */
  touch-action: manipulation;

  /* Branch selection mode highlight for expanded nodes */
  .branch-selected & {
    box-shadow:
      0 0 0 4px var(--color-primary, #6e53f9),
      0 0 12px 2px var(--color-primary-shadow, rgba(110, 83, 249, 0.35));
  }
`

// Wrapper for node with AI indicator positioned outside
// Sets CSS variables for zoom-independent element sizing:
// --zoom-scale: capped version for normal view (max 2.0)
// --zoom-scale-raw: uncapped version for collapsed view (true 1/zoom)
// Uses text-align: left to ensure content expands to the right
export const NodeWrapper = styled.div.attrs<{ $zoomScale: number; $zoomScaleRaw: number }>(
  (props) => ({
    style: {
      '--zoom-scale': props.$zoomScale,
      '--zoom-scale-raw': props.$zoomScaleRaw,
    } as React.CSSProperties,
  })
)`
  position: relative;
  display: inline-block;
  text-align: left;
`

// Lighter inset container for the node definition content (header + text + node-specific).
export const MainSection = styled.div<{
  $gradientColor: string
}>`
  background:
    linear-gradient(
      to right,
      ${(props) => hexToRgba(props.$gradientColor, 0.06)} 0%,
      ${(props) => hexToRgba(props.$gradientColor, 0.025)} 8%,
      ${(props) => hexToRgba(props.$gradientColor, 0.008)} 20%,
      transparent 35%
    ),
    var(--color-node-bg);
  border-radius: 6px;
  margin: 12px;
  overflow: visible;
  position: relative;
`

// Header container - transparent to let gradient show through
// When collapsed, minimal styling - just a simple inline container
export const HeaderContainer = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.$isCollapsed
      ? css`
          padding: 0;
          background-color: transparent;
          border-bottom: none;
        `
      : css`
          padding: ${SPACING.sm} ${SPACING.md};
          background-color: transparent;
          border-bottom: 1px solid var(--color-node-header-border);
        `}
  cursor: inherit;
`

// Header row - simple flex when collapsed
export const HeaderRow = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isCollapsed ? 'flex-start' : 'space-between')};
`

// Icon title group - uses CSS variable for zoom-independent scaling
// When NOT collapsed: uses transform for scaling (capped at 1.3)
// When collapsed: no transform needed - parent CollapsedViewWrapper handles scaling
export const IconTitleGroup = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$isCollapsed ? '4px' : SPACING.sm)};
  transform: ${(props) =>
    props.$isCollapsed ? 'none' : 'scale(calc(min(var(--zoom-scale, 1), 1.3) * 0.85))'};
  transform-origin: left center;
  flex-shrink: 0;
`

// Category title - styling only, scaling handled by parent IconTitleGroup
export const CategoryTitle = styled.span`
  font-size: ${TYPOGRAPHY.h4.fontSize};
  font-weight: ${600};
  line-height: ${TYPOGRAPHY.h4.lineHeight};
  color: var(--color-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

// Header actions group - uses CSS variable for zoom-independent scaling
// Higher cap (1.8) for buttons to stay clickable when zoomed out
export const HeaderActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  transform: scale(min(var(--zoom-scale, 1), 1.8));
  transform-origin: right center;
`

// Subheading container
export const SubheadingContainer = styled.div`
  font-size: ${TEXT_STYLES.secondarySmall.fontSize};
  color: var(--color-text-secondary);
  margin-top: ${SPACING.xs};
  line-height: ${TEXT_STYLES.secondarySmall.lineHeight};
`

// Content area - transparent to let gradient show, text stays readable
export const ContentArea = styled.div`
  padding: ${SPACING.sm} ${SPACING.md};
  color: var(--color-text-primary);
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex: 1;
  cursor: text;
  background-color: transparent;
`

// External labels container - positioned outside the card at bottom left
// Uses top: 100% so position is relative to card, then scales from top-left anchor
export const ExternalLabelsContainer = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  z-index: 5;
  transform: scale(var(--zoom-scale, 1));
  transform-origin: left top;
`

// Add label button - matches the new pill tag style
export const ExternalAddLabelButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--color-node-bg);
  border: 1px dashed var(--color-border-dark);
  border-radius: 16px;
  cursor: pointer;
  transition: all ${TRANSITIONS.fast};
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

  &:hover {
    border-color: var(--color-text-tertiary);
    color: var(--color-text-secondary);
    background: var(--color-bg-hover);
  }

  &:active {
    transform: scale(0.98);
  }
`
